import { useRef, ReactNode, KeyboardEvent } from "react";
import { twMerge } from "tailwind-merge";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

interface ThemePaddingInputProps {
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  icon?: ReactNode;
}

const ThemePaddingInput = ({ value, onChange, onBlur, icon }: ThemePaddingInputProps) => {
  const min = 0;
  const max = 60;
  const inputRef = useRef<HTMLInputElement>(null);

  const { isEditMode } = useThemePreviewContext();

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // do not allow these in number field
    if (["e", "E", ".", "-", "+"].includes(event.key)) {
      event.preventDefault();
    }
  };

  const containerStyles = twMerge(
    "flex items-center gap-1.5 rounded-md bg-white px-2.5 py-2",
    "outline outline-1 outline-neutrals-200 shadow-base",
    "focus-within:outline focus-within:outline-1 focus-within:outline-teal-700 focus-within:shadow-input",
    isEditMode && "hover:outline-teal-700",
    !isEditMode && "bg-neutrals-200 text-neutrals-500",
  );

  const inputStyles = twMerge(
    "flex-1 text-base pl-1 focus:outline-none",
    !isEditMode && "bg-neutrals-200",
  );

  const iconStyles = twMerge("pointer-events-none", !isEditMode && "text-neutrals-500");

  return (
    <div className={containerStyles}>
      {icon && <div className={iconStyles}>{icon}</div>}
      <input
        ref={inputRef}
        type="number"
        className={inputStyles}
        disabled={!isEditMode}
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onBlur?.(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export default ThemePaddingInput;
